<template>
  <div class="live-big-banner-cell thumb autoplay-cell instant-player-cell">
    <a
      aria-labelledby="autoplay-cell-title-1-1 autoplay-cell-title-1-2"
      class="thumb-image"
      @click="handleClick"
    >
      <div class="content-area">
        <WebpImage
          :image-info="{
            imageUrl: thumbnail,
            width: thumbnailInfo.width,
            height: thumbnailInfo.height,
            alt: altTitle,
            className: 'thumb-img',
          }"
        />
      </div>
      <div
        class="video-gradient"
        :style="{
          background: `linear-gradient(to right, ${bgColor ?? '#252525'} 0%, ${bgColor ?? '#252525'} 26%, rgba(0, 0, 0, 0) 100%)`,
        }"
      ></div>
      <div class="info-area">
        <!--rank image-->
        <img :src="rankImage" :alt="`랭킹 ${rank}위`" class="rank" />
        <!--channel-->
        <div
          v-if="logoImage"
          class="logo image"
          :style="{
            backgroundImage: `url(${resizedLogoImage})`,
          }"
          role="img"
          :aria-label="logoTitle"
        >
          <span class="ir">{{ logoTitle }}</span>
        </div>
        <div v-else class="logo text">
          {{ logoTitle }}
        </div>
        <!--program title-->
        <div v-if="programTitle" class="program-title">
          {{ programTitle }}
        </div>
        <!--ratings-->
        <div v-if="ratings !== undefined" class="ratings">
          시청 {{ ratings }}%
        </div>
        <!--progress bar-->
        <ProgressBar :ratio="progress" :style="{ marginTop: '20px' }" />
        <!--button-->
        <button v-if="buttonText" class="live-big-banner-more">
          {{ buttonText }}
        </button>
      </div>
      <LiveTopTag
        v-if="hasTopTagList"
        class="tag-area"
        :top-tag-list="topTagList"
        :animate="isActive"
      />
      <age-limits :target-age="targetAge" />
    </a>
  </div>
</template>
<script setup>
  import AgeLimits from '@/components/model/AgeLimits'
  import WebpImage from '@/components/image/WebpImage.vue'
  import { useCell } from '@/common/use/useCell'
  import LiveTopTag from '@/components/cell-fragment/LiveTopTag.vue'
  import { computed } from 'vue'
  import { resizeThumbnail } from '@/common/utils/image'
  import ProgressBar from '@/components/cell-fragment/ProgressBar.vue'

  const props = defineProps({
    cellInfo: {
      type: Object,
      default: null,
    },
    thumbnailInfo: {
      type: Object,
      default: null,
    },
    isActive: {
      type: Boolean,
      default: false,
    },
  })

  const {
    altTitle,
    buttonText,
    logoImage,
    logoTitle,
    progress,
    ratings,
    targetAge,
    thumbnail,
    titleList,
    topTagList,
    hasTopTagList,
    rank,
    bgColor,
    goDetail,
  } = useCell(props.cellInfo)

  const rankImage = computed(() => {
    const rankNumber = parseInt(rank.value, 10)
    if (!isNaN(rankNumber) && rankNumber >= 1 && rankNumber <= 20) {
      return require(
        `@/assets/images/main/landscape-rank/landscape-rank-${rankNumber}@3x.png`,
      )
    }
    return ''
  })
  const resizedLogoImage = computed(() => {
    return resizeThumbnail(logoImage.value, { width: 110 })
  })
  const programTitle = computed(() => titleList.value?.[0]?.text ?? '')

  const handleClick = () => {
    goDetail(props.cellInfo)
  }
</script>
<style scoped lang="scss">
  @import '@/assets/scss/import/_mixin.scss';
  @import '@/assets/scss/import/_color.scss';
  .info-area {
    position: absolute;
    left: 52px;
    display: flex;
    flex-flow: column;
    justify-content: center;
    align-items: flex-start;
    width: 22%;
    height: 100%;
    z-index: 10;
    transform: translateZ(0);
  }
  .content-area {
    position: absolute;
    top: 0;
    right: 0;
    width: 75%;
    height: 100%;
    cursor: pointer;
    overflow: hidden;

    picture {
      width: 100%;
      height: 100%;
      display: block;
    }

    :deep(.thumb-img) {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
    }

    .video-component-wrap {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      height: 100%;
      aspect-ratio: 16/9;
    }
  }
  .tag-area {
    position: absolute;
    top: 20px;
    left: 20px;
    z-index: 10;
    transform: translateZ(0);
  }
  .live-big-banner-cell {
    width: 100%;
    height: 100%;
    max-width: 1536px;
    min-width: 864px;
    .thumb-image {
      display: flex;
      justify-content: space-between;
      border-radius: 12px;
      overflow: hidden;
      position: relative;
      background-color: #252525;
      height: 100%;
    }
    .live-cell-container {
      display: flex;
    }
    ::v-deep(.tag-age) {
      top: 12px;
      right: 12px;
    }
  }
  .video-gradient {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 10;
    transform: translateZ(0);
  }
  .rank {
    height: 52px;
    width: auto;
  }
  .logo {
    margin-top: 8px;
    &.image {
      width: 110px;
      height: 44px;
      background-size: contain;
      background-repeat: no-repeat;
      background-position: left center;
    }
    &.text {
      color: $white;
      font-size: 20px;
      line-height: 28px;
      font-weight: 700;
    }
  }
  .program-title {
    @include Ellipsis($multi: true, $line: 2);
    color: $white;
    font-size: 22px;
    font-weight: 700;
    line-height: 32px;
    word-wrap: normal;
    word-break: keep-all;
    margin-top: 8px;
  }
  .ratings {
    color: $wh-trans-07;
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    margin-top: 4px;
  }
  .live-big-banner-more {
    width: fit-content;
    padding: 8px 16px;
    color: $white;
    font-size: 14px;
    line-height: 20px;
    background-color: rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    margin-top: 32px;
  }
</style>
